import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaBrain, FaPersonRunning } from "react-icons/fa6";
import { FiArrowRight } from "react-icons/fi";
import { usePublicLang } from "../../../config/i18n/public";

const CENTER_LOCK_PROGRESS = 0.36;
const ZOOM_START_PROGRESS = 0.18;
const ZOOM_END_PROGRESS = 0.7;
const GLASS_OVERLAY_FADE_IN_START = 0.62;
const GLASS_OVERLAY_FADE_IN_END = 0.74;
const GLASS_OVERLAY_FADE_OUT_START = 0.84;
const GLASS_OVERLAY_FADE_OUT_END = 0.88;
const CTA_FADE_IN_START = 0.88;
const CTA_FADE_IN_END = 0.92;
const MOBILE_ROUTINE_CARD_START = 0.66;
const MOBILE_ROUTINE_CARD_STEP = 0.065;
const MOBILE_ROUTINE_CARD_REVEAL_DURATION = 0.018;
const DEFAULT_GRID_GAP = 8;
const DEFAULT_TILE_RADIUS = 8;
const DEFAULT_OVERSCAN = 1.06;
const METRIC_EPSILON = 0.5;
const COLLAGE_IMAGE_PATHS = [
  "/homepage/collage/optimized/collage_01.webp",
  "/homepage/collage/optimized/collage_02.webp",
  "/homepage/collage/optimized/collage_03.webp",
  "/homepage/collage/optimized/collage_04.webp",
  "/homepage/collage/optimized/collage_05.webp",
  "/homepage/collage/optimized/collage_06.webp",
];

export const DEFAULT_SCROLL_FOCUS_COLLAGE_IMAGES = {
  topLeft: COLLAGE_IMAGE_PATHS[0],
  topRight: COLLAGE_IMAGE_PATHS[1],
  middleLeft: COLLAGE_IMAGE_PATHS[2],
  center: COLLAGE_IMAGE_PATHS[3],
  middleRight: COLLAGE_IMAGE_PATHS[4],
  bottomLeft: COLLAGE_IMAGE_PATHS[5],
  bottomRight: COLLAGE_IMAGE_PATHS[0],
};

function clamp(value, min = 0, max = 1) {
  return Math.min(Math.max(value, min), max);
}

function lerp(start, end, progress) {
  return start + (end - start) * progress;
}

function easeInOut(progress) {
  return progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;
}

function getSequentialOpacity(progress, start, step, revealDuration, index, count) {
  const itemStart = start + index * step;
  const nextItemStart = start + (index + 1) * step;
  const fadeIn = clamp((progress - itemStart) / revealDuration);
  const fadeOut =
    index === count - 1
      ? 1
      : clamp((nextItemStart + revealDuration - progress) / revealDuration);

  return Math.min(fadeIn, fadeOut);
}

function getSectionProgress(section) {
  const rect = section.getBoundingClientRect();
  const scrollableDistance = Math.max(section.offsetHeight - window.innerHeight, 1);
  return clamp(-rect.top / scrollableDistance);
}

function getCollageMetrics(target, startTopGap, overscan) {
  const targetCenterX = target.offsetLeft + target.offsetWidth / 2;
  const targetCenterY = target.offsetTop + target.offsetHeight / 2;
  const targetWidth = Math.max(target.offsetWidth, 1);
  const targetHeight = Math.max(target.offsetHeight, 1);

  return {
    centeredX: window.innerWidth / 2 - targetCenterX,
    centeredY: window.innerHeight / 2 - targetCenterY,
    startY: startTopGap,
    targetCenterX,
    targetCenterY,
    targetWidth,
    targetHeight,
    finalScale:
      Math.max(window.innerWidth / targetWidth, window.innerHeight / targetHeight) * overscan,
  };
}

function areMetricsEqual(a, b) {
  return Object.keys(b).every((key) => Math.abs(a[key] - b[key]) < METRIC_EPSILON);
}

function CollageTile({ src, className = "", targetRef = null, zoomProgress = 0 }) {
  const cornerRadius = lerp(DEFAULT_TILE_RADIUS, 0, zoomProgress);

  return (
    <figure
      ref={targetRef}
      className={[
        "m-0 h-full min-h-0 overflow-hidden bg-black shadow-[0_28px_80px_-52px_rgba(0,0,0,0.95)]",
        className,
      ].join(" ")}
      style={{ borderRadius: `${cornerRadius}px` }}
    >
      <img
        src={src}
        alt=""
        className="h-full w-full object-cover"
        draggable="false"
        loading="lazy"
        decoding="async"
      />
    </figure>
  );
}

export default function ScrollFocusCollage({
  images = DEFAULT_SCROLL_FOCUS_COLLAGE_IMAGES,
  className = "",
  gap = DEFAULT_GRID_GAP,
  startTopGap = null,
  overscan = DEFAULT_OVERSCAN,
  onWaitlistClick = null,
}) {
  const { copy } = usePublicLang();
  const routineCopy = copy.familyLanding.activationRoutine;
  const sectionRef = useRef(null);
  const frameRef = useRef(null);
  const centerTileRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [metrics, setMetrics] = useState({
    centeredX: 0,
    centeredY: 0,
    startY: 0,
    targetCenterX: 0,
    targetCenterY: 0,
    targetWidth: 1,
    targetHeight: 1,
    finalScale: 1,
  });

  const animation = useMemo(() => {
    const centerProgress = easeInOut(clamp(progress / CENTER_LOCK_PROGRESS));
    const zoomProgress = clamp(
      (progress - ZOOM_START_PROGRESS) / (ZOOM_END_PROGRESS - ZOOM_START_PROGRESS)
    );

    return {
      zoomProgress,
      scale: lerp(1, metrics.finalScale, zoomProgress),
      translateX: metrics.centeredX,
      translateY: lerp(metrics.startY, metrics.centeredY, centerProgress),
      gap: lerp(gap, 0, zoomProgress),
    };
  }, [gap, metrics, progress]);
  const centerOverlay = useMemo(() => {
    const width = metrics.targetWidth * animation.scale;
    const height = metrics.targetHeight * animation.scale;

    return {
      left: animation.translateX + metrics.targetCenterX - width / 2,
      top: animation.translateY + metrics.targetCenterY - height / 2,
      width,
      height,
      opacity: clamp((animation.zoomProgress - 0.015) / 0.05),
      borderRadius: lerp(DEFAULT_TILE_RADIUS, 0, animation.zoomProgress),
    };
  }, [animation, metrics]);
  const glassOverlayEntryOpacity = clamp(
    (progress - GLASS_OVERLAY_FADE_IN_START) /
      (GLASS_OVERLAY_FADE_IN_END - GLASS_OVERLAY_FADE_IN_START)
  );
  const glassOverlayExitProgress = clamp(
    (progress - GLASS_OVERLAY_FADE_OUT_START) /
      (GLASS_OVERLAY_FADE_OUT_END - GLASS_OVERLAY_FADE_OUT_START)
  );
  const glassOverlayOpacity = glassOverlayEntryOpacity * (1 - glassOverlayExitProgress);
  const ctaOpacity = clamp(
    (progress - CTA_FADE_IN_START) / (CTA_FADE_IN_END - CTA_FADE_IN_START)
  );
  const mobileRoutineCards = [
    {
      title: routineCopy.gamesTitle,
      description: routineCopy.gamesDescription,
      icon: <FaBrain aria-hidden className="h-12 w-12" />,
    },
    {
      title: routineCopy.physicalTitle,
      description: routineCopy.physicalDescription,
      icon: <FaPersonRunning aria-hidden className="h-12 w-12" />,
    },
    {
      title: routineCopy.companionTitle,
      description: routineCopy.companionDescription,
      icon: (
        <img
          src="/homepage/friends.png"
          alt=""
          className="h-14 w-14 object-contain drop-shadow-[0_4px_14px_rgba(125,211,252,0.45)]"
        />
      ),
    },
  ];
  const mobileRoutineCardOpacities = mobileRoutineCards.map((_, index) =>
    getSequentialOpacity(
      progress,
      MOBILE_ROUTINE_CARD_START,
      MOBILE_ROUTINE_CARD_STEP,
      MOBILE_ROUTINE_CARD_REVEAL_DURATION,
      index,
      mobileRoutineCards.length
    )
  );

  useEffect(() => {
    let scrollFrame = 0;
    let resizeFrame = 0;
    const initialTopGap = startTopGap ?? gap;

    function readMetrics() {
      const frame = frameRef.current;
      const target = centerTileRef.current;
      if (!frame || !target) return null;

      return getCollageMetrics(target, initialTopGap, overscan);
    }

    function updateMetrics() {
      const nextMetrics = readMetrics();
      if (!nextMetrics) return;

      setMetrics((currentMetrics) =>
        areMetricsEqual(currentMetrics, nextMetrics) ? currentMetrics : nextMetrics
      );
    }

    function updateProgress() {
      const section = sectionRef.current;
      if (!section) return;

      setProgress(getSectionProgress(section));
    }

    function updateLayoutAndProgress() {
      updateMetrics();
      updateProgress();
    }

    function onScroll() {
      window.cancelAnimationFrame(scrollFrame);
      scrollFrame = window.requestAnimationFrame(updateProgress);
    }

    function onResize() {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(updateLayoutAndProgress);
    }

    updateLayoutAndProgress();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.cancelAnimationFrame(scrollFrame);
      window.cancelAnimationFrame(resizeFrame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [gap, overscan, startTopGap]);

  return (
    <section
      ref={sectionRef}
      className={["relative h-[300lvh] bg-black sm:h-[740lvh]", className].join(" ")}
    >
      <div className="sticky top-0 h-[100dvh] overflow-hidden bg-black">
        <div
          className="absolute left-0 top-0 will-change-transform"
          style={{
            transform: `translate3d(${animation.translateX}px, ${animation.translateY}px, 0)`,
          }}
        >
          <div
            ref={frameRef}
            aria-hidden
            className="grid h-[150vh] will-change-transform"
            style={{
              gap: `${animation.gap}px`,
              gridTemplateColumns: "1.4fr 1.1fr 1.1fr 1.4fr",
              gridTemplateRows: "52vh 48vh 38vh",
              transform: `scale(${animation.scale})`,
              transformOrigin: `${metrics.targetCenterX}px ${metrics.targetCenterY}px`,
              width: "max(124vw, 1040px)",
            }}
          >
            <CollageTile src={images.topLeft} className="col-span-2" zoomProgress={animation.zoomProgress} />
            <CollageTile src={images.topRight} className="col-span-2" zoomProgress={animation.zoomProgress} />
            <CollageTile src={images.middleLeft} zoomProgress={animation.zoomProgress} />
            <CollageTile
              src={images.center}
              className="col-span-2"
              targetRef={centerTileRef}
              zoomProgress={animation.zoomProgress}
            />
            <CollageTile src={images.middleRight} zoomProgress={animation.zoomProgress} />
            <CollageTile src={images.bottomLeft} className="col-span-2" zoomProgress={animation.zoomProgress} />
            <CollageTile src={images.bottomRight} className="col-span-2" zoomProgress={animation.zoomProgress} />
          </div>
        </div>
        <figure
          aria-hidden
          className="pointer-events-none absolute m-0 overflow-hidden bg-black shadow-[0_28px_80px_-52px_rgba(0,0,0,0.95)]"
          style={{
            borderRadius: `${centerOverlay.borderRadius}px`,
            height: `${centerOverlay.height}px`,
            left: `${centerOverlay.left}px`,
            opacity: centerOverlay.opacity,
            top: `${centerOverlay.top}px`,
            width: `${centerOverlay.width}px`,
          }}
        >
          <img
            src={images.center}
            alt=""
            className="h-full w-full object-cover"
            draggable="false"
            decoding="async"
          />
        </figure>
        <aside
          className="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-[29%] flex-col justify-between border-r border-white/22 bg-[#081120]/40 px-6 py-10 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.28),inset_0_-44px_90px_rgba(255,255,255,0.04),24px_0_110px_-76px_rgba(0,0,0,0.95)] ring-1 ring-white/8 backdrop-blur-2xl md:flex sm:px-8 lg:px-10 lg:py-12"
          style={{
            opacity: glassOverlayOpacity,
            transform: `translateX(${(1 - glassOverlayOpacity) * -18}px)`,
          }}
        >
          <div className="mt-[26dvh]">
            <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-white/90 drop-shadow-[0_2px_14px_rgba(0,0,0,0.55)] sm:text-sm">
              {routineCopy.eyebrow}
            </p>
            <p className="mt-4 text-sm font-semibold leading-6 text-white/86 drop-shadow-[0_2px_14px_rgba(0,0,0,0.62)] sm:text-base sm:leading-7 lg:text-lg lg:leading-8">
              {routineCopy.description}
            </p>
          </div>
          <div>
            <div className="mb-6 h-[2px] w-full rounded-full bg-white/80 shadow-[0_0_18px_rgba(255,255,255,0.55)]" />
            <h3 className="text-3xl font-extrabold leading-tight tracking-[-0.01em] text-white drop-shadow-[0_3px_18px_rgba(0,0,0,0.68)] sm:text-4xl lg:text-5xl">
              {routineCopy.title}
            </h3>
          </div>
        </aside>
        <div
          className="hidden"
          style={{
            opacity: glassOverlayOpacity,
            transform: `translateY(${(1 - glassOverlayOpacity) * 18}px)`,
          }}
        >
          <p className="text-[0.7rem] font-extrabold uppercase tracking-[0.15em] text-white/90 drop-shadow-[0_2px_14px_rgba(0,0,0,0.55)]">
            {routineCopy.eyebrow}
          </p>
          <h3 className="mt-3 text-3xl font-extrabold leading-tight text-white drop-shadow-[0_3px_18px_rgba(0,0,0,0.68)]">
            {routineCopy.title}
          </h3>
          <p className="mt-3 text-sm font-semibold leading-6 text-white/86 drop-shadow-[0_2px_14px_rgba(0,0,0,0.62)]">
            {routineCopy.description}
          </p>
        </div>
        <div className="hidden">
          {mobileRoutineCards.map((card, index) => {
            const cardOpacity = (mobileRoutineCardOpacities[index] || 0) * glassOverlayOpacity;

            return (
              <div
                key={card.title}
                className="absolute inset-0 flex flex-col rounded-[1.5rem] border border-white/24 bg-white/[0.08] p-5 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.45),inset_0_-1px_0_rgba(255,255,255,0.08),0_30px_80px_-40px_rgba(0,0,0,0.78)] ring-1 ring-white/12 backdrop-blur-3xl"
                style={{
                  opacity: cardOpacity,
                  transform: `translateY(${(1 - cardOpacity) * 16}px) scale(${0.965 + cardOpacity * 0.035})`,
                }}
              >
                <span className="mb-4 flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl border border-white/30 text-[#e0f2fe] shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] ring-1 ring-white/10 drop-shadow-[0_4px_14px_rgba(224,242,254,0.5)]">
                  {card.icon}
                </span>
                <h3 className="text-xl font-extrabold leading-tight text-white drop-shadow-[0_3px_18px_rgba(0,0,0,0.68)]">
                  {card.title}
                </h3>
                <p className="mt-2 text-sm font-semibold leading-6 text-white/82 drop-shadow-[0_2px_14px_rgba(0,0,0,0.62)]">
                  {card.description}
                </p>
              </div>
            );
          })}
        </div>
        <div
          className="pointer-events-none absolute right-6 top-1/2 z-10 hidden w-[clamp(18rem,26vw,28rem)] flex-col gap-5 text-white md:flex lg:right-10"
          style={{
            opacity: glassOverlayOpacity,
            transform: `translateY(-50%) translateX(${(1 - glassOverlayOpacity) * 18}px)`,
          }}
        >
          <div className="flex h-[17rem] flex-col rounded-[1.75rem] border border-white/25 bg-white/[0.06] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.45),inset_0_-1px_0_rgba(255,255,255,0.08),0_30px_80px_-38px_rgba(0,0,0,0.7)] ring-1 ring-white/12 backdrop-blur-3xl lg:p-6">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-20 w-20 items-center justify-center rounded-2xl border border-white/30 text-[#e0f2fe] shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] ring-1 ring-white/10 drop-shadow-[0_4px_14px_rgba(224,242,254,0.6)]">
                <FaBrain aria-hidden className="h-14 w-14" />
              </span>
            </div>
            <h3 className="text-xl font-extrabold leading-tight text-white drop-shadow-[0_3px_18px_rgba(0,0,0,0.68)] lg:text-2xl">
              {routineCopy.gamesTitle}
            </h3>
            <p className="mt-2 text-sm font-semibold leading-6 text-white/82 drop-shadow-[0_2px_14px_rgba(0,0,0,0.62)] lg:text-base">
              {routineCopy.gamesDescription}
            </p>
          </div>
          <div className="flex h-[17rem] flex-col rounded-[1.75rem] border border-white/25 bg-white/[0.06] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.45),inset_0_-1px_0_rgba(255,255,255,0.08),0_30px_80px_-38px_rgba(0,0,0,0.7)] ring-1 ring-white/12 backdrop-blur-3xl lg:p-6">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-20 w-20 items-center justify-center rounded-2xl border border-white/30 text-[#e0f2fe] shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] ring-1 ring-white/10 drop-shadow-[0_4px_14px_rgba(224,242,254,0.6)]">
                <FaPersonRunning aria-hidden className="h-14 w-14" />
              </span>
            </div>
            <h3 className="text-xl font-extrabold leading-tight text-white drop-shadow-[0_3px_18px_rgba(0,0,0,0.68)] lg:text-2xl">
              {routineCopy.physicalTitle}
            </h3>
            <p className="mt-2 text-sm font-semibold leading-6 text-white/82 drop-shadow-[0_2px_14px_rgba(0,0,0,0.62)] lg:text-base">
              {routineCopy.physicalDescription}
            </p>
          </div>
          <div className="flex h-[17rem] flex-col rounded-[1.75rem] border border-white/25 bg-white/[0.06] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.45),inset_0_-1px_0_rgba(255,255,255,0.08),0_30px_80px_-38px_rgba(0,0,0,0.7)] ring-1 ring-white/12 backdrop-blur-3xl lg:p-6">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-20 w-20 items-center justify-center rounded-2xl border border-white/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] ring-1 ring-white/10">
                <img src="/homepage/friends.png" alt="" className="h-16 w-16 object-contain drop-shadow-[0_4px_14px_rgba(125,211,252,0.45)]" />
              </span>
            </div>
            <h3 className="text-xl font-extrabold leading-tight text-white drop-shadow-[0_3px_18px_rgba(0,0,0,0.68)] lg:text-2xl">
              {routineCopy.companionTitle}
            </h3>
            <p className="mt-2 text-sm font-semibold leading-6 text-white/82 drop-shadow-[0_2px_14px_rgba(0,0,0,0.62)] lg:text-base">
              {routineCopy.companionDescription}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onWaitlistClick || undefined}
          className="absolute left-1/2 top-16 z-10 hidden -translate-x-1/2 items-center gap-2 rounded-full bg-[#4285F4] px-5 py-3 text-base font-semibold text-white shadow-md transition hover:bg-[#357ae8] md:inline-flex"
          style={{
            opacity: ctaOpacity,
            pointerEvents: ctaOpacity > 0.6 ? "auto" : "none",
            transform: `translateX(-50%) translateY(${(1 - ctaOpacity) * 18}px)`,
          }}
        >
          {copy.familyLanding.login}
          <FiArrowRight aria-hidden className="h-5 w-5" />
        </button>
      </div>
    </section>
  );
}
